const WINDOW_KEY = '__lgProductSkuMap';

const EXTENDED_PRODUCTS_QUERY_PAGINATED = `query ExtendedProductsById($productIds: [Int!] $cursor: String) {site { products(entityIds: $productIds, after: $cursor) { edges { node { name path entityId sku variants(first: 25) { edges { node { entityId sku defaultImage { url(width: 320) } inventory { isInStock } productOptions(first: 10) { edges { node { entityId displayName ... on MultipleChoiceOption { displayStyle values(first: 18) { edges { node { entityId isDefault label ... on SwatchOptionValue { imageUrl(width: 30) } } } } } } } } prices { price { formatted value } retailPrice { value } salePrice { value } basePrice { value } } } } } } } pageInfo { hasNextPage endCursor } } } }`;

function ensureMap() {
    if (!(window[WINDOW_KEY] instanceof Map)) {
        window[WINDOW_KEY] = new Map();
    }
    return window[WINDOW_KEY];
}

export function setProductSkuMapEntries(entries) {
    if (!Array.isArray(entries)) return;
    const map = ensureMap();
    for (const entry of entries) {
        const id = Number(entry?.productId);
        if (!Number.isFinite(id)) continue;
        map.set(id, entry);
    }
}

export function getProductSkuMapEntry(productId) {
    const id = Number(productId);
    if (!Number.isFinite(id)) return undefined;
    return ensureMap().get(id);
}

export function getMotherShipSku(productId) {
    return getProductSkuMapEntry(productId)?.motherShipSku;
}

export async function getExtendedProducts(productIds, token) {
    let allMappedProducts = [];
    let hasNextPage = true;
    let currentCursor = null;
    while (hasNextPage) {
        const response = await fetch('/graphql', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ query: EXTENDED_PRODUCTS_QUERY_PAGINATED, variables: { productIds, cursor: currentCursor } }),
        });
        if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);
        const graphQLResponse = await response.json();
        if (graphQLResponse.errors) throw new Error(`GraphQL query failed: ${graphQLResponse.errors.map(e => e.message).join('\n')}`);
        const productsData = graphQLResponse.data?.site?.products;
        if (!productsData?.edges || productsData.edges.length === 0) {
            hasNextPage = false;
            continue;
        }
        const mappedPageProducts = productsData.edges.map(productEdge => {
            const product = productEdge.node;
            if (!product) return null;
            const defaultSwatchList = product.variants?.edges?.map(variantEdge => {
                const variant = variantEdge.node;
                if (!variant) return null;
                let swatchOptionNode = variant.productOptions?.edges?.find(opt => opt.node?.displayStyle === 'Swatch')?.node;
                if (typeof swatchOptionNode === 'undefined') {
                    swatchOptionNode = variant.productOptions?.edges?.find(opt => opt.node?.displayName === 'Color')?.node;
                }
                const swatchValueNode = swatchOptionNode?.values?.edges?.[0]?.node;
                return {
                    variantSku: variant.sku || "",
                    variantId: variant.entityId || 0,
                    productId: product.entityId || 0,
                    instock: variant?.inventory?.isInStock,
                    variantOptionId: swatchOptionNode?.entityId || 0,
                    variantOptionValueId: swatchValueNode?.entityId || 0,
                    variantIsDefault: swatchValueNode?.isDefault || false,
                    variantSwatchName: swatchValueNode?.label || "",
                    variantSwatchImage: swatchValueNode?.imageUrl || "",
                    variantProductImage: variant.defaultImage?.url || "",
                    variantFormattedPrice: variant.prices?.price?.formatted || "",
                    variantSalePrice: variant.prices?.salePrice?.value ?? 0.0,
                    variantRetailPrice: variant.prices?.retailPrice?.value ?? variant.prices?.basePrice?.value ?? 0.0,
                    variantURL: `${product.path}?sku=${variant.sku}` || "",
                };
            }).filter(Boolean) ?? [];
            return {
                motherShipSku: product.sku || "",
                productId: product.entityId || 0,
                name: product.name || "",
                path: product.path || "",
                defaultSwatchList,
                matchedVariant: null,
            };
        }).filter(Boolean);
        allMappedProducts.push(...mappedPageProducts);
        hasNextPage = productsData.pageInfo.hasNextPage;
        currentCursor = productsData.pageInfo.endCursor;
    }
    return allMappedProducts;
}

export async function fetchAndCacheProducts(productIds, token) {
    if (!Array.isArray(productIds) || productIds.length === 0) return [];
    const products = await getExtendedProducts(productIds, token);
    setProductSkuMapEntries(products);
    return products;
}
